"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SalesForceServer = void 0;
const location_1 = require("@/app/shared/constants/scheduler/location");
const helpers_1 = require("@/app/shared/helpers");
const constants_1 = require("@/app/shared/pages/redirect/constants");
const api_1 = require("@/app/SUMOCEP/server/api");
const token_1 = require("./token");
const utils_1 = require("@/app/SUMOCEP/utils");
const aws_amplify_1 = require("aws-amplify");
const generated_api_1 = require("./generated-api");
const utils_2 = require("./utils");
class SalesForceServerInstance {
    constructor() {
        Object.defineProperty(this, "exchangeCodeForTokens", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (params) => {
                return await helpers_1.Http.postJson((0, helpers_1.buildSumoAdminUrl)('/public/token/auth'), {
                    code: params.code,
                    code_verifier: params.codeVerifier,
                    domain: (0, token_1.toAuthDomain)(params.env),
                    redirect_uri: (0, helpers_1.getRedirectUrl)(location_1.IntegrationType.SALESFORCE),
                });
            }
        });
        Object.defineProperty(this, "getSalesforceAccessToken", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (payload) => {
                return new Promise(async (resolve, reject) => {
                    try {
                        const verifier = (0, helpers_1.generateCodeVerifier)();
                        const challenge = await (0, helpers_1.generateCodeChallenge)(verifier);
                        const state = Date.now();
                        const url = (0, helpers_1.buildSalesForceLoginUrl)(payload.env, '/services/oauth2/authorize', {
                            response_type: 'code',
                            client_id: SALESFORCE_CLIENT_ID,
                            code_challenge: challenge,
                            code_challenge_method: 'S256',
                            redirect_uri: (0, helpers_1.getRedirectUrl)(location_1.IntegrationType.SALESFORCE),
                            scope: 'api refresh_token',
                            prompt: 'login',
                            state,
                        });
                        const Popup = payload.isOutlookAddon ? helpers_1.OutlookPopup : helpers_1.WindowPopup;
                        const popupResponse = await Popup.open(url, { width: 640, height: 520 });
                        if (popupResponse instanceof helpers_1.PromiseTimeoutError || popupResponse instanceof Error) {
                            throw popupResponse;
                        }
                        Popup.onMessage(async (data) => {
                            if (data.type === constants_1.AuthRedirectEvent.Salesforce) {
                                if (data.code && data.state === state) {
                                    try {
                                        const tokens = await this.exchangeCodeForTokens({
                                            code: data.code,
                                            codeVerifier: verifier,
                                            env: payload.env,
                                        });
                                        (0, token_1.persistSalesForceTokens)(tokens, payload.env);
                                        resolve(tokens);
                                    }
                                    catch (error) {
                                        reject(error);
                                    }
                                }
                                else {
                                    reject(new Error('Salesforce authorization failed or was cancelled'));
                                }
                                Popup.close();
                            }
                        });
                    }
                    catch (error) {
                        reject(error);
                    }
                });
            }
        });
        Object.defineProperty(this, "loginWithSalesforceAuthorizationCode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (payload) => {
                return await this.getUser();
            }
        });
        Object.defineProperty(this, "login", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ code, integrationType, isAuthorized }) => {
                const response = await (0, helpers_1.handleAPIRequest)('/api/public/auth', {
                    code,
                    login: true,
                    redirectUrl: (0, helpers_1.getRedirectUrl)(integrationType),
                    switchAccount: false,
                    userTimeZone: 'Europe/Warsaw',
                    updateIntegration: false,
                    integrationType,
                });
                if (response && !isAuthorized) {
                    const cognitoUser = await aws_amplify_1.Auth.signIn(response.email);
                    await aws_amplify_1.Auth.sendCustomChallengeAnswer(cognitoUser, response.secret);
                }
                helpers_1.Storage.Local.set(helpers_1.Storage.Key.SERVER_TYPE, "SALESFORCE" /* ServerType.SALESFORCE */);
                return await this.getUser();
            }
        });
        Object.defineProperty(this, "isAuthorized", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                try {
                    if (!this.access_token) {
                        return false;
                    }
                    await (0, token_1.ensureValidSalesForceToken)();
                    return !!this.access_token;
                }
                catch (error) {
                    console.error(error);
                    return false;
                }
            }
        });
        Object.defineProperty(this, "logout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                const isShowGuide = helpers_1.Storage.Local.getBoolean(helpers_1.Storage.Key.SETTINGS_SHOW_GUIDE);
                helpers_1.Storage.Local.clearExcept(helpers_1.COGNITO_DEVICE_KEYS);
                if (isShowGuide !== undefined) {
                    helpers_1.Storage.Local.setBoolean(helpers_1.Storage.Key.SETTINGS_SHOW_GUIDE, isShowGuide);
                }
                return true;
            }
        });
        Object.defineProperty(this, "getUser", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                await (0, token_1.ensureValidSalesForceToken)();
                const url = (0, helpers_1.buildSalesForceInstanceUrl)(this.instanceUrl, '/services/oauth2/userinfo');
                const userInfo = await helpers_1.Http.postForm(url, {
                    access_token: this.access_token,
                    version: 'latest',
                });
                return {
                    id: userInfo.user_id,
                    name: userInfo.name,
                    email: userInfo.email,
                };
            }
        });
        Object.defineProperty(this, "getUserProfile", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                await (0, token_1.ensureValidSalesForceToken)();
                const url = (0, helpers_1.buildSalesForceInstanceUrl)(this.instanceUrl, '/services/oauth2/userinfo');
                const userInfo = await helpers_1.Http.postForm(url, {
                    access_token: this.access_token,
                    version: 'latest',
                });
                return {
                    userId: userInfo.user_id,
                    link: userInfo.profile,
                    fullName: userInfo.name,
                    email: userInfo.email,
                    avatar: userInfo.picture,
                    language: userInfo.language,
                    timeZone: userInfo.zoneinfo,
                };
            }
        });
        Object.defineProperty(this, "getAuthenticationResponse", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                const profile = await this.getUserProfile();
                return {
                    userData: profile,
                };
            }
        });
        Object.defineProperty(this, "getSchedulerData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (profile, serverPrefix, templateId) => {
                try {
                    const prefix = serverPrefix ? `${serverPrefix}__` : '';
                    const { data } = await api_1.API.v1.inboxInitList({ templateId });
                    const tools = await this.getTools(profile.userId, prefix);
                    if (!data)
                        throw new Error('No data');
                    const { attendees: attendeesData = [], hosts: hostsData = [], templates = [], services = [], locationOptions: locationOptionsData = [], userPreBufferTime, userPostBufferTime, globalPreBufferTime, globalPostBufferTime, } = data;
                    const { records = [] } = tools;
                    const attendees = (0, utils_2.getUsers)(attendeesData).filter((attendee) => Boolean(attendee.email));
                    const hosts = (0, utils_2.getUsers)(hostsData).filter((host) => Boolean(host.email));
                    const users = [...attendees, ...hosts];
                    const toolsOptions = (0, utils_2.createToolsResponse)(records, prefix).filter((tool, index, array) => index === array.findIndex((t) => t.id === tool.id));
                    const templatesOptions = (0, utils_2.getTemplateOptions)(templates, prefix).filter((template, index, array) => index === array.findIndex((t) => t.id === template.id));
                    const servicesOptions = (0, utils_2.getServiceOptions)(services, prefix);
                    const locationOptions = (0, utils_2.getLocationOptions)(locationOptionsData);
                    const response = {
                        userData: profile,
                        servicesOptions,
                        templatesOptions,
                        locationOptions,
                        toolsOptions,
                        hosts,
                        attendees,
                        users,
                        userPreBufferTime,
                        userPostBufferTime,
                        globalPreBufferTime,
                        globalPostBufferTime,
                    };
                    return response;
                }
                catch (error) {
                    throw error;
                }
            }
        });
        Object.defineProperty(this, "getHostSchedule", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (params, hostMeetings = [], template, serviceDurations) => {
                const { data } = await api_1.API.v1.inboxCalendarCreate((0, utils_2.createCalendarCreatePayload)(params, template, serviceDurations));
                return (0, utils_2.createAvailabilityResponse)(data, params, hostMeetings);
            }
        });
        Object.defineProperty(this, "getRoomAndResources", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (body, prefix) => {
                const [roomsResponse, resourcesResponse] = await Promise.all([
                    api_1.API.v1.inboxSearchCreate(generated_api_1.InboxSearchCreateParamsEnum.Rooms, body),
                    api_1.API.v1.inboxSearchCreate(generated_api_1.InboxSearchCreateParamsEnum.Resources, body),
                ]);
                const rooms = (0, utils_2.getOptions)(roomsResponse.data.results || [], prefix);
                const resources = (0, utils_2.getOptions)(resourcesResponse.data.results || [], prefix);
                return {
                    rooms,
                    resources,
                };
            }
        });
        Object.defineProperty(this, "schedulerUpsertInboxBookingPage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (params) => {
                try {
                    const response = await api_1.API.v1.inboxSaveLinkCreate((0, utils_2.createInboxSaveLinkCreatePayload)(params));
                    if (params?.bookingPage?.when?.customRanges?.length) {
                        await api_1.API.v1.inboxSaveCustomAvailabilityCreate((0, utils_2.createSaveCustomAvailabilityPayload)(params, response.data));
                    }
                    return response.data;
                }
                catch (error) {
                    console.log(`🚀 ~ SalesForceServerInstance ~ overrideschedulerUpsertInboxBookingPage ~ error:`, error);
                    throw error;
                }
            }
        });
        Object.defineProperty(this, "schedulerLinkInboxBookingPage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (params) => {
                try {
                    const response = await api_1.API.v1.inboxSaveAppointmentCreate((0, utils_2.createInboxSaveApptCreatePayload)(params));
                    return (0, utils_2.createInboxSaveApptCreateResponse)(response.data);
                }
                catch (error) {
                    console.log(`🚀 ~ SalesForceServerInstance ~ overrideschedulerLinkInboxBookingPage ~ error:`, error);
                    throw error;
                }
            }
        });
        Object.defineProperty(this, "getTools", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (organizerId, prefix) => {
                await (0, token_1.ensureValidSalesForceToken)();
                const url = `${this.instanceUrl}/services/data/v63.0/query/?q=SELECT+Id,${prefix}AppointmentType__c,${prefix}InviteLink__c,${prefix}Organizer__c+FROM+${prefix}Invite__c+WHERE+${prefix}Organizer__c='${organizerId}'`;
                return await helpers_1.Http.get(url, {
                    Authorization: `Bearer ${this.access_token}`,
                    [helpers_1.Http.HeaderName.CONTENT_TYPE]: helpers_1.Http.HeaderContentType.JSON,
                });
            }
        });
        Object.defineProperty(this, "getAttendees", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (payload) => {
                const { data } = await api_1.API.v1.inboxSearchCreate(generated_api_1.InboxSearchCreateParamsEnum.Attendees, payload);
                return (0, utils_2.getUsers)(data.results || []);
            }
        });
        Object.defineProperty(this, "getHost", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (payload) => {
                const { data } = await api_1.API.v1.inboxSearchCreate(generated_api_1.InboxSearchCreateParamsEnum.Hosts, payload);
                return (0, utils_2.getUsers)(data.results || []);
            }
        });
        Object.defineProperty(this, "getSlots", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (params) => {
                let hostSchedule = {
                    availability: params.hostScheduleAvailability,
                    meetings: params.hostScheduleMeetings,
                };
                if (params.availability === "CUSTOM" /* SchedulerAvailability.CUSTOM */) {
                    hostSchedule.availability = params.availabilityCustom.map((slot) => {
                        const bufferBefore = helpers_1.bufferTimeConvert.fromStringToMs(params.bookingPage.when?.beforeStartTime);
                        const bufferAfter = helpers_1.bufferTimeConvert.fromStringToMs(params.bookingPage.when?.afterEndTime);
                        return {
                            startsAt: new Date(slot.start).getTime() - bufferBefore,
                            finishesAt: new Date(slot.end).getTime() + bufferAfter,
                        };
                    });
                }
                else if (params.availability === "RANGE" /* SchedulerAvailability.RANGE */) {
                    hostSchedule = await this.getHostSchedule({
                        bookingPage: params.bookingPage,
                        startAt: params.calendarParameters.start.getTime(),
                        finishAt: params.calendarParameters.end.getTime(),
                    }, params.hostScheduleMeetings, params.template);
                }
                return (0, utils_1.generateSlots)(params.bookingPage, hostSchedule, params.template?.slotIntervalGranularity);
            }
        });
        Object.defineProperty(this, "getLocations", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (payload, prefix) => {
                const { data } = await api_1.API.v1.inboxSearchCreate(generated_api_1.InboxSearchCreateParamsEnum.Locations, payload);
                return (0, utils_2.getOptions)(data.results || [], prefix);
            }
        });
        Object.defineProperty(this, "getServices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (payload, prefix) => {
                const { data } = await api_1.API.v1.inboxSearchCreate(generated_api_1.InboxSearchCreateParamsEnum.Services, payload);
                return (0, utils_2.getServiceOptions)(data.results || [], prefix);
            }
        });
        Object.defineProperty(this, "getServiceData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (payload) => {
                const { data } = await api_1.API.v1.inboxServiceDataCreate(payload);
                return data?.serviceData;
            }
        });
        Object.defineProperty(this, "getPrefixRequest", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                await (0, token_1.ensureValidSalesForceToken)();
                const url = (0, helpers_1.buildSalesForceInstanceUrl)(this.instanceUrl, `/services/data/v63.0/query/?q=SELECT+NamespacePrefix+FROM+ApexClass+WHERE+Name+=+'SumoProvider'`);
                return await helpers_1.Http.get(url, { Authorization: `Bearer ${this.access_token}` });
            }
        });
        Object.defineProperty(this, "inboxInviteUrlList", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: api_1.API.v1.inboxInviteUrlList
        });
        Object.defineProperty(this, "inboxSearchCreate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: api_1.API.v1.inboxSearchCreate
        });
        Object.defineProperty(this, "inboxSaveLinkCreate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: api_1.API.v1.inboxSaveLinkCreate
        });
        Object.defineProperty(this, "inboxSaveAppointmentCreate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: api_1.API.v1.inboxSaveAppointmentCreate
        });
        Object.defineProperty(this, "inboxSaveCustomAvailabilityCreate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: api_1.API.v1.inboxSaveCustomAvailabilityCreate
        });
        Object.defineProperty(this, "inboxSearchParticipantsCreate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: api_1.API.v1.inboxSearchParticipantsCreate
        });
    }
    get access_token() {
        return helpers_1.Storage.Local.get(helpers_1.Storage.Key.SERVER_SALESFORCE_ACCESS_TOKEN) || '';
    }
    get refresh_token() {
        return helpers_1.Storage.Local.get(helpers_1.Storage.Key.SERVER_SALESFORCE_REFRESH_TOKEN) || '';
    }
    get accessTokenExpiration() {
        return helpers_1.Storage.Local.getNumber(helpers_1.Storage.Key.SERVER_SALESFORCE_ACCESS_TOKEN_EXPIRATION) || 0;
    }
    get refreshTokenExpiration() {
        return helpers_1.Storage.Local.getNumber(helpers_1.Storage.Key.SERVER_SALESFORCE_REFRESH_TOKEN_EXPIRATION) || 0;
    }
    get instanceUrl() {
        return helpers_1.Storage.Local.get(helpers_1.Storage.Key.SERVER_SALESFORCE_INSTANCE_URL) || '';
    }
}
exports.default = SalesForceServerInstance;
exports.SalesForceServer = new SalesForceServerInstance();
